<template>
  <div class="container-fluid customPadding">
    <div id="navigationButtons" class="mb-2">
      <b-button variant="link" class="text text-dark" @click="redirect('view')">Surveys</b-button
      ><b-icon icon="chevron-right" />
      <span class="text text-primary">{{ eventName }}</span>
      <b-button
        class="no-sm-float-right text text-primary"
        variant="link"
        @click="redirect('create')"
        ><b-icon icon="plus-circle"></b-icon> Create</b-button
      >
    </div>
    <div v-if="showInfo">
      <person-info :info="information"></person-info>
    </div>
    <div class="mt-5">
      <b-table
        :busy.sync="loading"
        :current-page="currentPage"
        :fields="getPersonSurveysFields()"
        :items="personSurveys"
        :per-page="perPage"
        bordered
        empty-text="There are no surveys to display."
        hover
        responsive="sm"
        show-empty
        head-variant="dark"
        primary-key="Id"
        striped
        borderless
        table-variant="light"
        class="rounded-lg d-none d-sm-block"
      >
        <template #table-busy>
          <div class="text-center text-secondary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>
        <template #cell(action)="row">
          <b-button variant="link-dark" @click="surveyStatus(row.item)">
            <div v-if="row.item.surveyStatus === 'Draft'">
              <b-icon icon="pencil-fill" />&nbsp;&nbsp;Edit
            </div>
            <div v-else><b-icon icon="eye-fill" />&nbsp;&nbsp;Show</div>
          </b-button>
        </template>
        <template #cell(surveyStatus)="row">
          <b-button disabled variant="outline-primary" style="background: white">{{
            row.item['surveyStatus']
          }}</b-button>
        </template>
        <template #cell(surveyLastModifiedDate)="data">
          {{ formatDate(data.value) }}
        </template>
      </b-table>
      <b-card
        class="mb-0 d-block d-sm-none"
        v-for="(item, idx) in displaySurveys"
        :key="idx + firstNumberOfPage"
        no-body
      >
        <b-card-header header-tag="header" class="pl-4" :style="bgc[idx % 2]">
          <b-row
            ><b-col class="pr-0">{{ formatDate(item['surveyLastModifiedDate']) }}</b-col></b-row
          >
          <b-row class="pb-3"
            ><b-col class="pr-0">{{ item['surveyCompletedBy'] }}</b-col>
          </b-row>
          <b-row
            ><b-col class="pr-0">
              <b-button disabled variant="outline-primary" style="background: white">{{
                item['surveyStatus']
              }}</b-button></b-col
            ><b-col class="pr-0">
              <b-button variant="link-dark" @click="surveyStatus(item)" class="float-right">
                <div v-if="item['surveyStatus'].includes('Draft')">
                  <b-icon icon="pencil-fill" />&nbsp;&nbsp;Edit
                </div>
                <div v-else><b-icon icon="eye-fill" />&nbsp;&nbsp;Show</div>
              </b-button>
            </b-col>
          </b-row>
        </b-card-header>
      </b-card>
      <no-data-card
        :loading="loading"
        :dataLength="displaySurveys.length"
        :message="'No surveys found for this site/study.'"
        :loadingMessage="'Loading Site Survey'"
      ></no-data-card>
      <b-pagination
        pills
        v-if="personSurveys.length > 0"
        v-model="currentPage"
        :per-page="perPage"
        :total-rows="personSurveys.length"
        align="center"
        first-number
        last-number
        page-class="pagination"
        class="mt-2"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
  import tableFieldsMixin from '../../mixins/tableFieldsMixin';
  import PersonInfo from './PersonInfo';
  import NoDataCard from '../../utility/NoDataCard';
  import moment from 'moment';
  import { mapActions } from 'vuex';

  export default {
    mixins: [tableFieldsMixin],
    components: {
      PersonInfo,
      NoDataCard,
    },
    metaInfo() {
      let metaTitle = this.$appName + this.eventName + ' - Surveys';
      return {
        title: metaTitle,
      };
    },
    data() {
      return {
        perPage: 7,
        currentPage: 1,
        personSurveys: [],
        loading: false,
        eventId: '',
        information: {},
        eventName: '',
        showInfo: false,
        firstNumberOfPage: 0,
        bgc: ['background-color:#FFFFFF', 'background-color:#F2EFFB'],
      };
    },
    watch: {
      $route() {
        this.setParameters();
        this.getPersonSurveys();
      },
    },
    computed: {
      displaySurveys() {
        let last =
          this.currentPage > Math.floor(this.rows / this.perPage)
            ? this.personSurveys.length
            : this.perPage * this.currentPage;
        let first = this.perPage * (this.currentPage - 1) + 1;
        return this.personSurveys.slice(first - 1, last);
      },
    },
    created() {
      this.setParameters();
      this.getPersonSurveys();
    },
    methods: {
      ...mapActions(['handleError']),
      setParameters() {
        this.eventId = this.$route.params.eventId;
      },
      async getPersonSurveys() {
        this.loading = true;
        try {
          let response = await axios.get(`/api/get-person-surveys/${this.eventId}`);
          this.personSurveys = response.data.surveys !== null ? response.data.surveys : [];
          this.information = response.data['refreshDetails']['response'];
          this.eventName = this.information['eventName'];
          this.showInfo = true;
        } catch (error) {
          await this.$store.dispatch('handleError', {
            vm: this,
            error: error,
            message: "Something went wrong while fetching the person's surveys.",
          });
        }
        this.loading = false;
      },
      redirect(to) {
        if (to === 'create') {
          this.$router.push({
            name: 'create-person-survey',
            params: { eventId: this.eventId },
          });
        } else {
          this.$router.push({ name: 'surveys' });
        }
      },
      surveyStatus(survey) {
        if (survey.surveyStatus === 'Draft') {
          this.$router.push({
            name: 'create-person-survey-from-view-all',
            params: { eventId: this.eventId, surveyId: survey.surveyId },
          });
        } else {
          this.$router.push({
            name: 'view-person-submitted-survey',
            params: {
              eventId: this.eventId,
              surveyId: survey.surveyId,
              info: 'Person Info',
            },
          });
        }
      },
      formatDate(date) {
        if (date == null || date === '') {
          return '';
        } else {
          return moment(date).format('MMM DD YYYY HH:mm');
        }
      },
    },
  };
</script>
<style>
  .text {
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.25px;
  }
</style>
